import React from 'react';
import axios from 'axios';
import { useParams, useLocation } from 'react-router-dom';
import { Button, Spinner, useToast, Group } from '@economic/taco';
import { Field } from '@economic/taco';

import { Column, Row } from '../../../app/shared/components/Grid/Grid';
import { createJsonPatchOperation } from '../../../app/shared/util/api/internalApiClient';
import { t, useBaseL10n } from '../../../app/shared/components/l10n/l10n';
import { useValidatedState } from '../../../app/shared/hooks/useValidateState';
import Viewer from '../../../app/pages/modules/inbox/components/Viewer/Viewer';
import { Logo } from '../../../app/components/Logo/Logo';
import { ExternalDocument, DocumentApprovalStatus } from '../../../app/pages/modules/inbox/types';
import Approved from './icons/Approved';
import Rejected from './icons/Rejected';
import './style.css';
import { NotFound } from '../NotFound';

interface Props {
    externalDocument: ExternalDocument;
    publicKey: string;
    refresh: () => void;
}

const maxLengthValidator = maxLength => value => (value ? value.length <= maxLength : false);

function Document({ externalDocument, publicKey, refresh }: Props) {
    const document = externalDocument.document;
    const [note, setNote, isNoteValid] = useValidatedState<string>(document.note, maxLengthValidator(250));
    const toast = useToast();

    React.useEffect(() => {
        window.document.title = `e-conomic - ${document.name}`;
    }, [document.id]);

    const saveNote = async () => {
        const toastRef = toast.loading(t`TOAST_LOADING`);

        try {
            await axios.patch(`${process.env.ECONOMIC_PATH}/internal-api/external/documents/${publicKey}`, [
                createJsonPatchOperation('replace', '/note', note),
            ]);
            toastRef.success(t`TOAST_DOCUMENT_CHANGE_NOTE_SUCCESS`);
        } catch (error) {
            toastRef.error(error);
        }
    };

    const approveDocument = async () => {
        const toastRef = toast.loading(t`TOAST_LOADING`);

        try {
            await axios.post(`${process.env.ECONOMIC_PATH}/internal-api/document-approval/${publicKey}`, {
                approvalStatus: 'Approved',
            });
            refresh();
            toastRef.success(t`TOAST_DOCUMENT_APPROVE_SUCCESS`);
        } catch (error) {
            toastRef.error(error);
        }
    };

    const rejectDocument = async () => {
        const toastRef = toast.loading(t`TOAST_LOADING`);

        try {
            await axios.post(`${process.env.ECONOMIC_PATH}/internal-api/document-approval/${publicKey}`, {
                approvalStatus: 'Rejected',
            });
            refresh();
            toastRef.success(t`TOAST_DOCUMENT_REJECT_SUCCESS`);
        } catch (error) {
            toastRef.error(error);
        }
    };
    const isApproved = document.approval && document.approval.status === DocumentApprovalStatus.Approved;
    const isRejected = document.approval && document.approval.status === DocumentApprovalStatus.Rejected;

    return (
        <>
            <Column xs="8">
                <Viewer document={{ publicKey, ...document }} isPopup isDraggable={false} />
            </Column>
            <Column xs="4">
                <div className="external__document__approval">
                    {isApproved && (
                        <h1>
                            {<Approved />}
                            {' ' + t`DOCUMENT_APPROVAL_ACTION_STATUS_APPROVED`}
                        </h1>
                    )}
                    {isRejected && (
                        <h1>
                            {<Rejected />}
                            {' ' + t`DOCUMENT_APPROVAL_ACTION_STATUS_REJECTED`}
                        </h1>
                    )}

                    {(isApproved || isRejected) && (
                        <dl>
                            <dt>{t`DOCUMENT_APPROVAL_INFO_DOCUMENT_NAME`}</dt>
                            <dd>{document.name}</dd>

                            <dt>{t`DOCUMENT_APPROVAL_INFO_AGREEMENT_NAME`}</dt>
                            <dd>{externalDocument.companyName}</dd>

                            <dt>{t`DOCUMENT_APPROVAL_INFO_AGREEMENT_NUMBER`}</dt>
                            <dd>{externalDocument.agreementNumber}</dd>

                            <dt>{t`DOCUMENT_APPROVAL_INFO_APPROVED_BY`}</dt>
                            <dd>{document.approval?.approverName}</dd>

                            <dt>{t`DOCUMENT_APPROVAL_INFO_DATE`}</dt>
                            <dd>{document.approval?.updatedAt || undefined}</dd>
                        </dl>
                    )}
                    {document.approval && document.approval.status === DocumentApprovalStatus.Pending && (
                        <>
                            <h2> {t`DOCUMENT_APPROVAL_ACTION_DESCRIPTION`}</h2>
                            <p>{document.name}</p>
                            <Group>
                                <Button onClick={approveDocument} appearance="primary">
                                    {t`DOCUMENT_APPROVAL_ACTION_BUTTON_APPROVE`}
                                </Button>
                                <Button onClick={rejectDocument} appearance="default">
                                    {t`DOCUMENT_APPROVAL_ACTION_BUTTON_REJECT`}
                                </Button>
                            </Group>
                        </>
                    )}
                </div>

                <div className="external__document__note">
                    <Field
                        message={!isNoteValid ? t`DOCUMENT_INBOX_FORM_DESCRIPTION_MAXIMUM_EXCEEDED` : undefined}
                        invalid={!isNoteValid}>
                        {t`EXTERNAL_DOCUMENT_VIEW_NOTE_INSTRUCTIONS`}
                        <textarea
                            wrap="hard"
                            className="gd-textarea"
                            onChange={event => {
                                setNote(event.target.value);
                            }}
                            placeholder={t`EXTERNAL_DOCUMENT_VIEW_NOTE_FIELD_PLACEHOLDER`}
                            value={note}
                        />
                    </Field>
                    <Button appearance="primary" onClick={saveNote}>
                        {t`EXTERNAL_DOCUMENT_VIEW_NOTE_BUTTON_SAVE`}
                    </Button>
                </div>
            </Column>
        </>
    );
}

export default function ExternalDocumentPage() {
    const { key } = useParams();
    const { search } = useLocation();
    const language = new URLSearchParams(search).get('language') || 'da-DK';
    const loadingTexts = useBaseL10n(textIDs, language);
    const [documentQuery, setDocumentQuery] = React.useState<any>({ loading: true, error: undefined, data: {} });
    const fetch = () => {
        axios
            .get(`${process.env.ECONOMIC_PATH}/internal-api/external/documents/${key}`)
            .then(response => setDocumentQuery({ loading: false, error: undefined, data: response.data }))
            .catch(error => setDocumentQuery({ loading: false, error, data: undefined }));
    };
    React.useEffect(fetch, []);

    if (!key || documentQuery.error) {
        return <NotFound />;
    }

    return (
        <div id="external__document" className="gd-container">
            <Row>
                <Column>
                    <Logo />
                </Column>
            </Row>
            <Row>
                {loadingTexts || documentQuery.loading ? (
                    <Spinner />
                ) : (
                    <Document externalDocument={documentQuery.data} publicKey={key} refresh={fetch} />
                )}
            </Row>
        </div>
    );
}

const textIDs = [
    'TOAST_LOADING',
    'TOAST_DOCUMENT_CHANGE_NOTE_SUCCESS',
    'TOAST_DOCUMENT_APPROVE_SUCCESS',
    'TOAST_DOCUMENT_REJECT_SUCCESS',
    'EXTERNAL_DOCUMENT_VIEW_TITLE',
    'EXTERNAL_DOCUMENT_VIEW_NOTE_INSTRUCTIONS',
    'EXTERNAL_DOCUMENT_VIEW_NOTE_FIELD_PLACEHOLDER',
    'EXTERNAL_DOCUMENT_VIEW_NOTE_BUTTON_SAVE',
    'DOCUMENT_INBOX_DOCUMENT_TOOLTIP_PRINT_DOCUMENT',
    'DOCUMENT_INBOX_DOCUMENT_TOOLTIP_GOTO_FIRST_PAGE',
    'DOCUMENT_INBOX_DOCUMENT_TOOLTIP_GOTO_PREVIOUS_PAGE',
    'DOCUMENT_MANAGEMENT_DOCUMENT_LIST_PAGE_X_OF_Y',
    'DOCUMENT_INBOX_DOCUMENT_TOOLTIP_GOTO_NEXT_PAGE',
    'DOCUMENT_INBOX_DOCUMENT_TOOLTIP_GOTO_LAST_PAGE',
    'DOCUMENT_INBOX_FORM_DESCRIPTION_MAXIMUM_EXCEEDED',
    'DOCUMENT_APPROVAL_ACTION_DESCRIPTION',
    'DOCUMENT_APPROVAL_ACTION_BUTTON_APPROVE',
    'DOCUMENT_APPROVAL_ACTION_BUTTON_REJECT',
    'DOCUMENT_APPROVAL_ACTION_STATUS_APPROVED',
    'DOCUMENT_APPROVAL_ACTION_STATUS_REJECTED',
    'DOCUMENT_APPROVAL_INFO_DOCUMENT_NAME',
    'DOCUMENT_APPROVAL_INFO_AGREEMENT_NAME',
    'DOCUMENT_APPROVAL_INFO_AGREEMENT_NUMBER',
    'DOCUMENT_APPROVAL_INFO_APPROVED_BY',
    'DOCUMENT_APPROVAL_INFO_REJECTED_BY',
    'DOCUMENT_APPROVAL_INFO_DATE',
];
